import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setCalendarData, addSchedule, updateSchedule, removeSchedule } from '../store/calendarSlice';
import { setProductData } from '../store/productSlice';
import { setAdminData } from '../store/adminSlice';
import { PW_Calendar_API, PW_Products_API, PW_Stores_API, PW_Calendar_Save_API, PW_Calendar_Delete_API } from '../Server/ApiV2';
import Spinner from './Spinner';
import { startingTypeOrder } from '../utils/typeOrder';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const pad2 = (n) => String(n).padStart(2, '0');
const day10 = (v) => (v || '').slice(0, 10);
const keyOf = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const todayKey = () => keyOf(new Date());
const nextDay = (key) => {
  const d = new Date(`${key}T00:00`);
  d.setDate(d.getDate() + 1);
  return keyOf(d);
};
const fmtDate = (key, withYear = true) => new Date(`${key}T00:00`).toLocaleDateString('en-US',
  withYear ? { month: 'short', day: 'numeric', year: 'numeric' } : { month: 'short', day: 'numeric' });
const fmtRange = (s) => {
  const start = day10(s.startDate);
  const end = day10(s.endDate);
  return start === end ? fmtDate(start) : `${fmtDate(start)} – ${fmtDate(end)}`;
};
const plural = (n, one, many) => (n === 1 ? one : many);
const failText = (res) => res?.data?.message || res?.response?.data?.message || res?.message || '';

export default function ScheduleCalendar() {
  const dispatch = useDispatch();
  const { schedules } = useSelector(s => s.calendar);
  const { stores } = useSelector(s => s.admin);
  const { products, productTypes, productTypeLinks } = useSelector(s => s.product);

  const today = todayKey();
  const thisYear = Number(today.slice(0, 4));
  const [pageLoading, setPageLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [loadCount, setLoadCount] = useState(0);
  const [storeId, setStoreId] = useState('');
  const [productId, setProductId] = useState('');
  const [year, setYear] = useState(thisYear);
  const [month, setMonth] = useState(Number(today.slice(5, 7)));
  const [editor, setEditor] = useState(null);
  const [editorError, setEditorError] = useState('');
  const [replaceList, setReplaceList] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [notice, setNotice] = useState(null);
  const messageRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setPageLoading(true);
    (async () => {
      const [calRes, prodRes, storeRes] = await Promise.all([
        PW_Calendar_API(), PW_Products_API(), PW_Stores_API(),
      ]);
      if (cancelled) return;
      if (calRes?.data?.success) dispatch(setCalendarData(calRes.data.data));
      if (prodRes?.data?.success) dispatch(setProductData(prodRes.data.data));
      if (storeRes?.data?.success) dispatch(setAdminData({ stores: storeRes.data.data }));
      // With a stale or missing schedule list a day could look free, and + Add would
      // then create a second schedule for a day that already has one.
      setLoadError(!calRes?.data?.success || !prodRes?.data?.success || !storeRes?.data?.success);
      setPageLoading(false);
    })();
    return () => { cancelled = true; };
  }, [dispatch, loadCount]);

  useEffect(() => {
    if (notice?.type !== 'success') return undefined;
    const timer = setTimeout(() => setNotice(null), 5000);
    return () => clearTimeout(timer);
  }, [notice]);

  // The message sits under the type list, below the fold of a long dialog on small screens.
  useEffect(() => {
    if (replaceList || editorError || confirmDelete) {
      messageRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
    }
  }, [replaceList, editorError, confirmDelete]);

  const activeStores = stores.filter(s => s.status === 'Active');
  const activeProducts = products.filter(p => p.status === 'Active');
  const activeTypes = productTypes.filter(t => t.status === 'Active');
  const viewStore = activeStores.some(s => s.id === storeId) ? storeId : (activeStores[0]?.id || '');
  const viewProduct = activeProducts.some(p => p.id === productId) ? productId : (activeProducts[0]?.id || '');

  const linkedTypesOf = (productIds) => [...new Set(productTypeLinks
    .filter(l => l.linked && productIds.includes(l.productId)).map(l => l.typeId))];
  const viewLinked = linkedTypesOf([viewProduct]);
  const nameList = (ids, list) => ids.map(id => list.find(x => x.id === id)?.name || id).join(', ');
  const typeTitle = (typeId) => {
    const t = productTypes.find(pt => pt.id === typeId);
    return [t?.name, t?.label].filter(Boolean).join(' · ') || typeId;
  };

  const viewScheds = schedules.filter(s => s.status === 'Active'
    && (s.storeIds || []).includes(viewStore)
    && (!s.productIds?.length || s.productIds.includes(viewProduct)));

  const daysInMonth = new Date(year, month, 0).getDate();
  const leadingBlanks = new Date(year, month - 1, 1).getDay();
  const days = Array.from({ length: daysInMonth }, (_, i) => {
    const key = `${year}-${pad2(month)}-${pad2(i + 1)}`;
    // Coupon generation uses the newest schedule when two overlap, so that one is listed first.
    const list = viewScheds
      .filter(s => day10(s.startDate) <= key && key <= day10(s.endDate))
      .sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')));
    return { day: i + 1, key, weekday: (leadingBlanks + i) % 7, list };
  });
  const scheduledDays = days.filter(d => d.list.length > 0).length;

  const yearOptions = [...new Set([thisYear - 1, thisYear, thisYear + 1, thisYear + 2, year])].sort((a, b) => a - b);
  const shiftMonth = (delta) => {
    const d = new Date(year, month - 1 + delta, 1);
    setYear(d.getFullYear());
    setMonth(d.getMonth() + 1);
  };
  const goToday = () => {
    setYear(thisYear);
    setMonth(Number(today.slice(5, 7)));
  };

  const openNew = (key) => {
    setEditor({
      id: null, status: 'Active', startDate: key, endDate: key,
      storeIds: [viewStore], productIds: [viewProduct],
      productTypeOrder: startingTypeOrder(viewProduct, linkedTypesOf([viewProduct])),
    });
  };

  const openEdit = (sched) => {
    const productIds = [...(sched.productIds || [])];
    let order = [...(sched.productTypeOrder || [])];
    if (!order.length && productIds.length) order = linkedTypesOf(productIds.slice(0, 1));
    setEditor({
      id: sched.id, status: sched.status,
      startDate: day10(sched.startDate), endDate: day10(sched.endDate),
      origStart: day10(sched.startDate), origEnd: day10(sched.endDate),
      storeIds: [...(sched.storeIds || [])], productIds, productTypeOrder: order,
    });
  };

  const closeEditor = () => {
    setEditor(null);
    setEditorError('');
    setReplaceList(null);
    setConfirmDelete(false);
  };

  const changeEditor = (patch) => {
    setEditorError('');
    setReplaceList(null);
    setConfirmDelete(false);
    setEditor(prev => ({ ...prev, ...(typeof patch === 'function' ? patch(prev) : patch) }));
  };
  const addType = (typeId) => changeEditor(prev => ({ productTypeOrder: [...prev.productTypeOrder, typeId] }));
  const removeType = (idx) => changeEditor(prev => ({ productTypeOrder: prev.productTypeOrder.filter((_, i) => i !== idx) }));
  const moveType = (idx, dir) => changeEditor(prev => {
    const order = [...prev.productTypeOrder];
    const target = idx + dir;
    if (target < 0 || target >= order.length) return {};
    [order[idx], order[target]] = [order[target], order[idx]];
    return { productTypeOrder: order };
  });

  const dateProblem = (() => {
    if (!editor) return '';
    if (!editor.startDate || !editor.endDate) return 'Choose both dates.';
    if (editor.startDate > editor.endDate) return 'The To date cannot be before the From date.';
    return '';
  })();
  const canSave = !!editor && !dateProblem && editor.storeIds.length > 0
    && editor.productTypeOrder.length > 0 && !saving && !deleting;
  const editorTypeIds = editor
    ? (editor.productIds.length ? linkedTypesOf(editor.productIds) : activeTypes.map(t => t.id))
    : [];
  const addableTypes = activeTypes.filter(t => editorTypeIds.includes(t.id));

  // Per 100 coupons, the same maths as the Calendar Settings form — keep the two in step.
  const economics = (() => {
    const prod = editor && products.find(p => editor.productIds.includes(p.id));
    if (!prod) return null;
    const factor = prod.priceAmount || 1;
    const scale = 100 / (prod.cardsPerBundle || 100);
    const isAny2 = (prod.matchingMethod || prod.winCondition) === 'any2';
    const sell = prod.rsp * 100;
    const prizes = editor.productTypeOrder.map(typeId => {
      const link = productTypeLinks.find(l => l.productId === prod.id && l.typeId === typeId && l.linked);
      const config = link?.winnerConfig || {};
      const winners = Object.values(config).reduce((sum, c) => sum + (c || 0), 0);
      const configPrize = Object.entries(config).reduce((sum, [d, c]) => sum + Number(d) * (c || 0), 0) * factor;
      const minGuarantee = isAny2 ? (100 - Math.round(winners * scale)) * 20 * factor : 0;
      return Math.round(configPrize * scale) + minGuarantee;
    });
    const totalSell = sell * prizes.length;
    const totalPrize = prizes.reduce((sum, p) => sum + p, 0);
    return { sell, prizes, totalSell, totalPrize, totalPnl: totalSell - totalPrize };
  })();

  const overlapsOf = (ed) => schedules.filter(s => s.id !== ed.id && s.status === 'Active'
    && (s.storeIds || []).some(sid => ed.storeIds.includes(sid))
    && (!ed.productIds.length || !s.productIds?.length || s.productIds.some(pid => ed.productIds.includes(pid)))
    && ed.startDate <= day10(s.endDate) && ed.endDate >= day10(s.startDate));

  // Replacing is only safe when the saved schedule fully covers the old one; otherwise
  // deleting it would also drop days, stores or products the new schedule doesn't cover.
  const coveredBy = (s, ed) => day10(s.startDate) >= ed.startDate && day10(s.endDate) <= ed.endDate
    && (s.storeIds || []).every(sid => ed.storeIds.includes(sid))
    && (!ed.productIds.length || (s.productIds?.length > 0 && s.productIds.every(pid => ed.productIds.includes(pid))));

  const handleSave = async (replaceConfirmed) => {
    if (!canSave) return;
    const overlaps = overlapsOf(editor);
    const blocking = overlaps.filter(s => !coveredBy(s, editor));
    if (blocking.length) {
      setReplaceList(null);
      setEditorError(`These dates overlap ${plural(blocking.length, 'a schedule that also covers', 'schedules that also cover')} other days, stores or products: ${blocking.map(fmtRange).join(', ')}. Change the dates, or change ${plural(blocking.length, 'that schedule', 'those schedules')} first.`);
      return;
    }
    if (overlaps.length && !replaceConfirmed) {
      setEditorError('');
      setReplaceList(overlaps);
      return;
    }

    const data = {
      id: editor.id || `CAL-${Date.now()}`,
      dateMode: 'range',
      startDate: editor.startDate,
      endDate: editor.endDate,
      status: editor.status || 'Active',
      storeIds: editor.storeIds,
      productIds: editor.productIds,
      productTypeOrder: editor.productTypeOrder,
    };
    setSaving(true);
    setEditorError('');
    const res = await PW_Calendar_Save_API(data);
    if (!res?.data?.success) {
      setSaving(false);
      setReplaceList(null);
      setEditorError(`Could not save the schedule. Nothing was changed. ${failText(res)}`.trim());
      return;
    }
    if (editor.id) dispatch(updateSchedule(data));
    else dispatch(addSchedule({ ...data, createdAt: new Date().toISOString() }));

    const notDeleted = [];
    for (const s of overlaps) {
      const del = await PW_Calendar_Delete_API(s.id);
      if (del?.data?.success) dispatch(removeSchedule(s.id));
      else notDeleted.push(s);
    }
    setSaving(false);
    closeEditor();
    const saved = fmtRange(data);
    if (notDeleted.length) {
      setNotice({
        type: 'warning',
        text: `Schedule saved for ${saved}, but ${plural(notDeleted.length, 'this overlapping schedule', 'these overlapping schedules')} could not be deleted: ${notDeleted.map(fmtRange).join(', ')}. Please delete ${plural(notDeleted.length, 'it', 'them')} in Calendar Settings.`,
      });
    } else {
      const replaced = overlaps.length ? ` Replaced ${overlaps.length} overlapping ${plural(overlaps.length, 'schedule', 'schedules')}.` : '';
      setNotice({ type: 'success', text: `Schedule saved for ${saved}.${replaced}` });
    }
  };

  const savedSched = editor?.id ? schedules.find(s => s.id === editor.id) : null;

  // What deleting does to selling: only today and later matter, and a day that
  // another schedule still covers keeps selling.
  const deleteImpact = (s) => {
    const end = day10(s.endDate);
    const from = day10(s.startDate) > today ? day10(s.startDate) : today;
    if (!end || from > end) return "It has already ended, so deleting it doesn't change any sales.";
    const others = overlapsOf({ id: s.id, storeIds: s.storeIds || [], productIds: s.productIds || [], startDate: from, endDate: end });
    let days = 0;
    let open = 0;
    for (let key = from; key <= end; key = nextDay(key)) {
      days++;
      if (!others.some(o => day10(o.startDate) <= key && key <= day10(o.endDate))) open++;
    }
    if (!open) {
      return days === 1
        ? 'Another schedule covers that day, so coupons will keep selling.'
        : 'Other schedules cover all its remaining days, so coupons will keep selling.';
    }
    let when;
    if (days === 1) when = from === today ? 'today' : 'that day';
    else if (open === days) when = `on its ${days} remaining days`;
    else when = `on ${open} of its ${days} remaining days`;
    return `Coupons can't be sold ${when} until you add another schedule.`;
  };

  const askDelete = () => {
    setEditorError('');
    setReplaceList(null);
    setConfirmDelete(true);
  };

  const handleDelete = async () => {
    if (!savedSched || saving || deleting) return;
    setDeleting(true);
    setEditorError('');
    const res = await PW_Calendar_Delete_API(savedSched.id);
    setDeleting(false);
    if (!res?.data?.success) {
      setConfirmDelete(false);
      setEditorError(`Could not delete the schedule. Nothing was changed. ${failText(res)}`.trim());
      return;
    }
    dispatch(removeSchedule(savedSched.id));
    closeEditor();
    setNotice({ type: 'success', text: `Schedule deleted for ${fmtRange(savedSched)}.` });
  };

  if (pageLoading) return <div className="page-loading"><Spinner /></div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>Schedule Calendar</h2>
          <div className="page-header-sub">See and edit each store's schedule on the calendar</div>
        </div>
      </div>

      {notice && (
        <div className={`schedcal-notice ${notice.type}`} role="status">
          <span>{notice.text}</span>
          <button type="button" className="schedcal-notice-close" onClick={() => setNotice(null)} aria-label="Dismiss">×</button>
        </div>
      )}

      {loadError ? (
        <div className="card">
          <div className="card-body empty-state">
            <p className="mb-16">Could not load the schedules, stores or products, so the calendar can't be shown.</p>
            <button type="button" className="btn btn-primary" onClick={() => setLoadCount(c => c + 1)}>Try Again</button>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="card-title gold">
            {MONTHS[month - 1]} {year}
            {viewStore && viewProduct && (
              <span className="schedcal-count">{scheduledDays} of {daysInMonth} days scheduled</span>
            )}
          </div>
          <div className="card-body">
            <div className="schedcal-toolbar">
              <label className="schedcal-field">
                <span className="text-sm text-muted">Store</span>
                <select className="form-select" value={viewStore} onChange={e => setStoreId(e.target.value)}>
                  {activeStores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </label>
              <label className="schedcal-field">
                <span className="text-sm text-muted">Product</span>
                <select className="form-select" value={viewProduct} onChange={e => setProductId(e.target.value)}>
                  {activeProducts.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </label>
              <div className="schedcal-nav">
                <button type="button" className="btn btn-outline btn-sm" onClick={() => shiftMonth(-1)} aria-label="Previous month">‹</button>
                <select className="form-select" value={month} onChange={e => setMonth(Number(e.target.value))} aria-label="Month">
                  {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                </select>
                <select className="form-select" value={year} onChange={e => setYear(Number(e.target.value))} aria-label="Year">
                  {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
                <button type="button" className="btn btn-outline btn-sm" onClick={() => shiftMonth(1)} aria-label="Next month">›</button>
                <button type="button" className="btn btn-outline btn-sm schedcal-today-btn" onClick={goToday}>Today</button>
              </div>
            </div>
            <div className="text-sm text-muted mb-16">
              Click a schedule to change or delete it, or <strong>+ Add</strong> on a day that has none.
            </div>

            {!viewStore || !viewProduct ? (
              <div className="empty-state">
                <p>{!viewStore ? 'There are no active stores.' : 'There are no active products.'}</p>
              </div>
            ) : (
              <div className="schedcal-grid">
                {DAYS.map(d => <div key={d} className="schedcal-weekday">{d}</div>)}
                {Array.from({ length: leadingBlanks }, (_, i) => <div key={`blank-${i}`} className="schedcal-blank" />)}
                {days.map(d => {
                  const past = d.key < today;
                  const cls = ['schedcal-day',
                    (d.weekday === 0 || d.weekday === 6) && 'weekend',
                    past && 'past',
                    d.key === today && 'today'].filter(Boolean).join(' ');
                  return (
                    <div key={d.key} className={cls} role="group" aria-label={fmtDate(d.key)}>
                      <div className="schedcal-daynum">
                        <span>{d.day}<span className="schedcal-dayname"> {DAYS[d.weekday]}</span></span>
                        {d.key === today && <span className="schedcal-today-tag">Today</span>}
                      </div>
                      {d.list.map((s, i) => {
                        const types = (s.productTypeOrder || []).filter(t => viewLinked.includes(t));
                        return (
                          <button key={s.id} type="button" className="schedcal-sched" onClick={() => openEdit(s)}
                            aria-label={`Edit schedule ${fmtRange(s)}: ${types.join(', ') || 'no types set'}`}>
                            <span className="schedcal-types">
                              {types.length ? types.map((t, j) => (
                                <React.Fragment key={`${t}-${j}`}>
                                  {j > 0 && <span className="cal-arrow">→</span>}
                                  <span className="schedcal-type" title={typeTitle(t)}>{t}</span>
                                </React.Fragment>
                              )) : <span className="schedcal-warn">No types set</span>}
                            </span>
                            {day10(s.startDate) !== day10(s.endDate) && (
                              <span className="schedcal-range">
                                {fmtDate(day10(s.startDate), false)} – {fmtDate(day10(s.endDate), false)}
                              </span>
                            )}
                            {d.list.length > 1 && (
                              <span className={i === 0 ? 'schedcal-inuse' : 'schedcal-warn'}>
                                {i === 0 ? 'In use' : 'Overlap, not used'}
                              </span>
                            )}
                            <span className="schedcal-edit">Edit</span>
                          </button>
                        );
                      })}
                      {!d.list.length && !past && (
                        <>
                          <span className="schedcal-warn">No schedule</span>
                          <button type="button" className="schedcal-add" onClick={() => openNew(d.key)}
                            aria-label={`Add schedule for ${fmtDate(d.key)}`}>+ Add</button>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {editor && (
        <div className="modal-overlay">
          <div className="modal-content" role="dialog" aria-modal="true" aria-labelledby="schedcal-editor-title">
            <div className="modal-header">
              <span id="schedcal-editor-title">{editor.id ? 'Edit Schedule' : 'New Schedule'}</span>
              <button type="button" className="modal-close" onClick={closeEditor} disabled={saving || deleting} aria-label="Close">×</button>
            </div>
            <div className="modal-body">
              <div className="schedcal-facts">
                <div>
                  <span className="text-sm text-muted">Store</span>
                  <strong>{nameList(editor.storeIds, stores)}</strong>
                </div>
                <div>
                  <span className="text-sm text-muted">Product</span>
                  <strong>{editor.productIds.length ? nameList(editor.productIds, products) : 'All products'}</strong>
                </div>
              </div>
              {editor.id && editor.origStart !== editor.origEnd && (
                <div className="schedcal-callout">
                  This schedule covers {fmtDate(editor.origStart)} – {fmtDate(editor.origEnd)}. Changes apply to every day in it.
                </div>
              )}
              {editor.id && editor.origStart <= today && today <= editor.origEnd && (
                <div className="schedcal-callout warn">
                  This schedule covers today. Changes apply from the next coupon sold.
                </div>
              )}

              <div className="form-row">
                <div>
                  <label className="form-label" htmlFor="schedcal-from">From</label>
                  <input id="schedcal-from" type="date" className="form-input" value={editor.startDate}
                    onChange={e => changeEditor({ startDate: e.target.value })} />
                </div>
                <div>
                  <label className="form-label" htmlFor="schedcal-to">To</label>
                  <input id="schedcal-to" type="date" className="form-input" value={editor.endDate}
                    min={editor.startDate || undefined}
                    onChange={e => changeEditor({ endDate: e.target.value })} />
                </div>
              </div>
              {dateProblem && <div className="schedcal-error">{dateProblem}</div>}

              <div className="form-label mt-16">Product Type Hierarchy</div>
              <div className="text-sm text-muted mb-8">
                Types run in this order, 100 coupons each. A type can be added more than once.
              </div>
              <div className="schedcal-addtypes">
                {addableTypes.map(t => (
                  <button key={t.id} type="button" className="btn btn-outline btn-sm" onClick={() => addType(t.id)}>
                    + {t.name}
                  </button>
                ))}
              </div>
              {editor.productTypeOrder.length === 0 ? (
                <div className="schedcal-error">Add at least one product type.</div>
              ) : (
                <div className="cal-type-order">
                  {editor.productTypeOrder.map((typeId, idx) => {
                    const t = productTypes.find(pt => pt.id === typeId);
                    const prize = economics ? economics.prizes[idx] : 0;
                    return (
                      <div key={`${typeId}-${idx}`} className="cal-type-item schedcal-type-row">
                        <span className="cal-type-num">{idx + 1}</span>
                        <span className="fw-600">{t?.name || typeId}</span>
                        {t?.label && <span className="text-sm text-muted">{t.label}</span>}
                        {!editorTypeIds.includes(typeId) && <span className="chip chip-red">Not linked, skipped</span>}
                        {economics && (
                          <span className="schedcal-money">
                            <span className="sell">Sell <strong>{economics.sell.toLocaleString()}</strong></span>
                            <span className="prize">Prize <strong>{prize.toLocaleString()}</strong></span>
                            <span className={economics.sell - prize >= 0 ? 'gain' : 'loss'}>
                              P&amp;L <strong>{(economics.sell - prize).toLocaleString()}</strong>
                            </span>
                          </span>
                        )}
                        <span className="schedcal-row-actions">
                          <button type="button" className="btn btn-sm btn-secondary cal-move-btn"
                            disabled={idx === 0} onClick={() => moveType(idx, -1)} aria-label={`Move ${typeId} up`}>↑</button>
                          <button type="button" className="btn btn-sm btn-secondary cal-move-btn"
                            disabled={idx === editor.productTypeOrder.length - 1} onClick={() => moveType(idx, 1)}
                            aria-label={`Move ${typeId} down`}>↓</button>
                          <button type="button" className="btn btn-sm cal-remove-btn"
                            onClick={() => removeType(idx)} aria-label={`Remove ${typeId}`}>×</button>
                        </span>
                      </div>
                    );
                  })}
                  {economics && (
                    <div className="schedcal-total">
                      <span className="fw-600">Total ({editor.productTypeOrder.length} × 100 coupons)</span>
                      <span className="schedcal-money">
                        <span className="sell">Sell <strong>{economics.totalSell.toLocaleString()}</strong></span>
                        <span className="prize">Prize <strong>{economics.totalPrize.toLocaleString()}</strong></span>
                        <span className={economics.totalPnl >= 0 ? 'gain' : 'loss'}>
                          P&amp;L <strong>{economics.totalPnl.toLocaleString()}</strong>
                        </span>
                      </span>
                    </div>
                  )}
                </div>
              )}

              <div ref={messageRef}>
                {replaceList && (
                  <div className="schedcal-callout danger" role="alert">
                    <div className="fw-600">
                      {plural(replaceList.length, 'This schedule', `These ${replaceList.length} schedules`)} for the same store and product {plural(replaceList.length, 'falls', 'fall')} inside the new dates and will be deleted:
                    </div>
                    <ul className="schedcal-replace-list">
                      {replaceList.map(s => (
                        <li key={s.id}>{fmtRange(s)}: {(s.productTypeOrder || []).join(' → ') || 'no types'}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {confirmDelete && savedSched && (
                  <div className="schedcal-callout danger" role="alert">
                    <div className="fw-600">Delete this schedule?</div>
                    <div>
                      {nameList(savedSched.storeIds || [], stores)} · {savedSched.productIds?.length ? nameList(savedSched.productIds, products) : 'All products'}, {fmtRange(savedSched)}.
                      {' '}{deleteImpact(savedSched)}
                    </div>
                  </div>
                )}
                {editorError && <div className="schedcal-error" role="alert">{editorError}</div>}
              </div>
            </div>
            <div className="modal-footer schedcal-footer">
              {confirmDelete ? (
                <>
                  <button type="button" className="btn btn-secondary" onClick={() => setConfirmDelete(false)} disabled={deleting}>
                    Keep Schedule
                  </button>
                  <button type="button" className="btn btn-danger" onClick={handleDelete} disabled={deleting}>
                    {deleting ? 'Deleting…' : 'Yes, Delete'}
                  </button>
                </>
              ) : (
                <>
                  {savedSched && (
                    <button type="button" className="btn schedcal-delete-btn" onClick={askDelete} disabled={saving}>Delete</button>
                  )}
                  <button type="button" className="btn btn-secondary" onClick={closeEditor} disabled={saving}>Cancel</button>
                  {replaceList ? (
                    <button type="button" className="btn btn-danger" onClick={() => handleSave(true)} disabled={!canSave}>
                      {saving ? 'Saving…' : `Delete ${replaceList.length} & Save`}
                    </button>
                  ) : (
                    <button type="button" className="btn btn-success" onClick={() => handleSave(false)} disabled={!canSave}>
                      {saving ? 'Saving…' : (editor.id ? 'Update Schedule' : 'Save Schedule')}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
